import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import currency from 'currency.js';
import styles from './FreeShippingProgress.module.sass';

// The "You're $12.01 away from FREE shipping" bar at the top of the order
// summary (Katie, 2026-10-21: the Amazon / Target / Old Navy treatment), for a
// cart whose coupon waives shipping over a subtotal (FREE_SHIPPING_OVER and
// PERCENTAGE_WITH_FREE_SHIPPING_OVER).
//
// Pure: everything it says comes from the server's cart payload --
// `subtotalWithoutCoupon` (what the threshold is judged on, before the coupon
// and before shipping), the offer's `freeShippingMinimum`, and
// `amountToFreeShipping` / `waivesShipping`, which are the SAME numbers
// Cart#waives_shipping? charges by. It never re-decides whether shipping is
// free; it only draws how close the cart is.
const TruckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5" />
    <path d="M3 9l4 0" />
  </svg>
);

export default function FreeShippingProgress({ progress }) {
  if (!progress) return null;
  const {
    minimum, subtotal, remaining, reached,
  } = progress;
  const percent = reached ? 100 : Math.max(0, Math.min(100, (subtotal / minimum) * 100));
  // Whole dollars read cleaner on the scale; the remaining amount keeps cents
  // because it is the number the parent will shop to.
  const minimumLabel = minimum % 1 === 0 ? `$${minimum}` : currency(minimum).format();

  return (
    <div className={cn(styles.wrapper, reached && styles.reached)} data-testid="free-shipping-progress">
      <div className={styles.line}>
        <TruckIcon />
        {reached ? (
          <span>You&rsquo;ve unlocked FREE shipping!</span>
        ) : (
          <span>
            You&rsquo;re
            {' '}
            <strong className={styles.amount}>{currency(remaining).format()}</strong>
            {' '}
            away from FREE shipping
          </span>
        )}
      </div>
      <div
        className={styles.track}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        aria-label="Progress to free shipping"
      >
        <div className={styles.fill} style={{ width: `${percent}%` }} />
      </div>
      <div className={styles.scale}>
        <span>{currency(subtotal).format()}</span>
        <span>{minimumLabel}</span>
      </div>
    </div>
  );
}

FreeShippingProgress.propTypes = {
  // From couponSummary(cart).freeShippingProgress; null when the cart's coupon
  // has no threshold.
  progress: PropTypes.shape({
    minimum: PropTypes.number.isRequired,
    subtotal: PropTypes.number.isRequired,
    remaining: PropTypes.number.isRequired,
    reached: PropTypes.bool.isRequired,
  }),
};

FreeShippingProgress.defaultProps = { progress: null };
